/**
 * Toast d'alerte "table de défi prête" pour les défis (CIA, défi libre,
 * défi par équipe) créés automatiquement par le serveur Java legacy.
 *
 * Contrairement aux invitations sociales (InvitationToast), il n'y a ni
 * accepter/refuser ni délai de réponse : l'utilisateur a déjà pris
 * l'engagement en amont (défi lancé, match programmé). Le toast propose
 * de rejoindre la table en un clic ; "Plus tard" le masque temporairement
 * (il réapparaît tout seul) et repousse la connexion automatique de
 * sécurité qui se déclenche si la table n'a toujours pas été rejointe.
 */

import { useEffect, useState } from 'react';
import type { DefiTableAlert } from '../hooks/useNotificationSocket';

/** N'afficher le compte à rebours qu'à l'approche de la connexion automatique */
const COUNTDOWN_WARNING_THRESHOLD_MS = 60_000;

function formatCountdown(ms: number): string {
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return minutes > 0 ? `${minutes}:${seconds.toString().padStart(2, '0')}` : `${seconds}s`;
}

function defiAlertTitle(alert: DefiTableAlert): string {
  switch (alert.defiKind) {
    case 'cia': return 'Votre défi CIA vous attend';
    case 'championnat_equipe': return 'Votre match de championnat vous attend';
    case 'par_equipe': return 'Votre défi par équipe vous attend';
    case 'libre':
    case 'ouvert':
      return 'Votre défi libre vous attend';
    default:
      return `${alert.defiKindLabel || 'Défi'} — une table vous attend`;
  }
}

function defiAlertDetails(alert: DefiTableAlert): string {
  const colonIndex = alert.nomtable?.indexOf(':') ?? -1;
  const matchup = colonIndex >= 0 ? alert.nomtable!.slice(colonIndex + 1).trim() : '';
  return matchup || alert.comm || 'La partie peut commencer dès que vous rejoignez.';
}

function formatRdvTime(rdv: string): string | null {
  const date = new Date(rdv);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

interface DefiTableAlertToastProps {
  alert: DefiTableAlert;
  onJoin: (alert: DefiTableAlert) => void;
  onDismiss: (tableKey: string) => void;
}

function DefiTableAlertToast({ alert, onJoin, onDismiss }: DefiTableAlertToastProps) {
  const [now, setNow] = useState(() => Date.now());
  const remainingMs = alert.forceJoinAt - now;
  const showCountdown = remainingMs > 0 && remainingMs <= COUNTDOWN_WARNING_THRESHOLD_MS;
  const rdvTime = alert.rdv ? formatRdvTime(alert.rdv) : null;

  useEffect(() => {
    if (remainingMs > COUNTDOWN_WARNING_THRESHOLD_MS) return;
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alert.forceJoinAt]);

  return (
    <div className={`invitation-toast defi-table-alert-toast ${showCountdown ? 'defi-table-alert-toast-urgent' : ''}`}>
      <div className="invitation-toast-content">
        <div className="invitation-toast-icon">🏆</div>
        <div className="invitation-toast-info">
          <div className="invitation-toast-title">{defiAlertTitle(alert)}</div>
          <div className="invitation-toast-details">{defiAlertDetails(alert)}</div>
          {rdvTime && (
            <div className="invitation-toast-hint">Rendez-vous à {rdvTime}</div>
          )}
          {showCountdown && (
            <div className="defi-table-alert-countdown">
              Connexion automatique dans {formatCountdown(remainingMs)}…
            </div>
          )}
        </div>
      </div>

      <div className="invitation-toast-actions">
        <button className="invitation-accept-btn" onClick={() => onJoin(alert)}>
          Rejoindre
        </button>
        <button className="invitation-decline-btn" onClick={() => onDismiss(alert.tableKey)}>
          Plus tard
        </button>
      </div>
    </div>
  );
}

interface DefiTableAlertContainerProps {
  alerts: DefiTableAlert[];
  onJoin: (alert: DefiTableAlert) => void;
  onDismiss: (tableKey: string) => void;
}

export function DefiTableAlertContainer({ alerts, onJoin, onDismiss }: DefiTableAlertContainerProps) {
  if (alerts.length === 0) return null;

  return (
    <div className="defi-table-alert-container">
      {alerts.map(alert => (
        <DefiTableAlertToast
          key={alert.tableKey}
          alert={alert}
          onJoin={onJoin}
          onDismiss={onDismiss}
        />
      ))}
    </div>
  );
}
